import React, { useCallback, useState } from "react";
import {
    Box,
    ButtonBase,
    Collapse,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import QRCode from "react-qr-code";
import LinkRounded from "@mui/icons-material/LinkRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import ShareRounded from "@mui/icons-material/ShareRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRounded from "@mui/icons-material/ExpandLessRounded";
import { CARD_STYLES } from "../constants/styles";

interface Props {
    url: string;
    label?: string;
    showUrl?: boolean;
}

const ShareableLink: React.FC<Props> = ({
    url,
    label = "Link",
    showUrl = true,
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const [copied, setCopied] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);

    const canShare = typeof navigator.share === "function";

    const copyToClipboard = useCallback(() => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [url]);

    const shareLink = useCallback(async () => {
        try {
            await navigator.share({ title: label, url });
        } catch {
            /* user cancelled */
        }
    }, [url, label]);

    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    return (
        <Stack sx={CARD_STYLES} spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={iconBoxSx}>
                    <LinkRounded sx={{ fontSize: 18, color: primary }} />
                </Box>
                <Typography
                    fontWeight={400}
                    fontSize="12px"
                    lineHeight="18px"
                    color="text.secondary"
                >
                    {label}
                </Typography>
            </Stack>

            {showUrl && (
                <Typography
                    fontWeight={600}
                    fontSize="14px"
                    color={primary}
                    sx={{ wordBreak: "break-all", overflow: "hidden" }}
                >
                    {url}
                </Typography>
            )}

            <Stack direction="row" alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
                <ButtonBase
                    onClick={copyToClipboard}
                    sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: "12px",
                        bgcolor: copied
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(primary, 0.08),
                        transition: "background-color 0.2s ease",
                        gap: 0.75,
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    {copied ? (
                        <CheckRounded
                            sx={{ fontSize: 16, color: theme.palette.success.main }}
                        />
                    ) : (
                        <ContentCopyRounded
                            sx={{ fontSize: 16, color: primary }}
                        />
                    )}
                    <Typography
                        fontWeight={500}
                        fontSize="13px"
                        color={copied ? theme.palette.success.main : primary}
                        sx={{ transition: "color 0.2s ease" }}
                    >
                        {copied ? "Copied!" : "Copy link"}
                    </Typography>
                </ButtonBase>

                {canShare && (
                    <ButtonBase
                        onClick={shareLink}
                        sx={{
                            px: 2,
                            py: 0.75,
                            borderRadius: "12px",
                            bgcolor: alpha(primary, 0.08),
                            gap: 0.75,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <ShareRounded sx={{ fontSize: 16, color: primary }} />
                        <Typography
                            fontWeight={500}
                            fontSize="13px"
                            color={primary}
                        >
                            Share
                        </Typography>
                    </ButtonBase>
                )}

                <ButtonBase
                    onClick={() => setQrOpen(!qrOpen)}
                    sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: "12px",
                        bgcolor: alpha(primary, 0.08),
                        gap: 0.5,
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <QrCode2Rounded sx={{ fontSize: 16, color: primary }} />
                    {qrOpen ? (
                        <ExpandLessRounded sx={{ fontSize: 16, color: primary }} />
                    ) : (
                        <ExpandMoreRounded sx={{ fontSize: 16, color: primary }} />
                    )}
                </ButtonBase>
            </Stack>

            <Collapse in={qrOpen}>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: "16px",
                        bgcolor: alpha(primary, 0.04),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <QRCode
                        value={url}
                        size={180}
                        bgColor="transparent"
                        fgColor={darken(primary, 0.15)}
                    />
                </Box>
            </Collapse>
        </Stack>
    );
};

export default ShareableLink;
